/**
 * The PDApps shell (#1484) — a program of its own on the taskbar (and a phone
 * tab): every PDApp the person can get back to, as a card collection.
 *
 *   Recent     — every PDApp they opened, in ANY org or through a link (personal:
 *                it follows the account, not the active org), newest first: ONE row
 *                of as many cards as fit the window; "View all" opens the Recent page.
 *   Favorites  — "your deck": what they starred (stored as pins), holographic;
 *                never ages out. Empty slots invite the next star.
 *   ‹Org›      — the collection: the PDApps they can use in the org they're in now
 *                and the links that name them — chips All · Yours · Shared with me ·
 *                Links (All by default), sorted A→Z (the default) / Recently opened / Newest.
 *                An app that appeared since their last visit wears "New" (this
 *                browser remembers what it has shown — see collection.ts).
 *
 * The page's light takes the colours of the card under the pointer or keyboard focus (on
 * a phone, the card in the middle of the screen); on a visit's first load the cards are
 * dealt in. The arrow keys move between cards (Enter or Space turns one over, `/`
 * searches). The Favorites deck can be arranged; its order is the person's own, saved
 * with their pins (PUT /pins/order). With a mouse, a ▶ on the card under the pointer
 * runs its app in one click (PROTOTYPE). On a phone, what to show and the sort are in a
 * bottom sheet.
 *
 * The Recent page (`…/apps/recent`, inside this same window) lists the whole
 * history with filter chips (All · this org · other orgs · links), Show more,
 * and Clear history.
 *
 * Every card says where its app is from: the line under its name names the source
 * ("Acme · 2h ago", "Public link · …"); an app from somewhere other than where the
 * person is now — opening it switches org — also wears that org's badge. Link apps
 * wear the link badge.
 *
 * A click on a card lifts it into the middle of the screen and turns it over: its back
 * holds the details and Run (which bounces the card twice, then opens the app), Share,
 * Favorite, Remove (history entries). Share opens the small Share modal
 * (AppShareDialog). A LINK app offers nothing that opens its files: a link share
 * means "run it, don't read its internals". Everything else stays on Drive → PDApps,
 * which the header links to.
 *
 * Every list arrives judged by the server, and Run / Share go through the routes
 * and dialogs they always did — this view decides no access.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Search, User, UsersRound, Star, Clock, Play, Share2, ArrowUpRight, ArrowLeft, Trash2, Link2, Globe,
  ChevronRight, ChevronDown, Building2, Smartphone, LayoutGrid, Check, Move, Plus, ArrowUpDown, MoreHorizontal,
} from 'lucide-react'
import { isMiniappFolder, isSharedPointer, stripPointerSuffix } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { usePageTitle } from '@papan/brand/react'
import { ShareLinksDialog } from '@papan/share-links/ui'
import {
  PDAppIcon, FileContextMenu, useAnchoredPosition, useRefreshWhenShown, useTheme, getThemeClass, useConfirm, useToast, type FileMenuItem,
} from '@papan/core-ui/ui'
import { useProjects, useIncomingSharedProjects, type ProjectItem } from '../home/data'
import { useProjectCardActions, type FolderPicker } from '../home/useProjectCardActions'
import { buildProjectMenuItems } from '../home/projectMenu'
import type { ProjectCardHandlers } from '../home/ProjectCard'
import { formatRelativeDate } from '../formatters'
import {
  appName, buildOrgApps, filterEntries, fetchHistoryPage, usePdappHistory, useSharedLinkApps,
  openLinkApp, pinActiveApp, pinSharedLink, setEntryPinned, showPinOrder, putPinOrder, reloadHistory, reloadHistoryOnly, removeEntry, clearHistory, locateLinkApp, takeLocateFragment,
  recentFilterOf, RECENT_FILTERS, type RecentFilter, type PdappEntry,
} from './appsData'
import { AppShareDialog, type AppShareTarget } from './AppShareDialog'
import { WhoHasAccessDialog } from '../WhoHasAccessDialog'
import { canManageAccessFor } from '../fileActions'
import { usePermission } from '@papan/files/client/queries'
import { PdappCard, GhostCard, CardInspect, CardFloat, KindIcon, type CardView, type CardAction, type CardDetail, type CardFavorite, type CardKind, type CardLabels } from './PdappCard'
import { DeckGrid } from './DeckGrid'
import { FilterSheet } from './FilterSheet'
import { useNewProject } from '../NewProjectContext'
import { nextCardIndex, orderDeck, NAV_KEYS, type NavKey } from './cardNav'
import {
  cardColors, sortCards, newCards, rememberSeen, readSeen, writeSeen, readPref, writePref,
  CARD_W, COLLECTION_SORTS, type CollectionSort,
} from './collection'
import { useGyroTilt } from './useCardTilt'

/** The launch bounce's length — `.apps-launch-bounce` in index.css (two bounces). */
const LAUNCH_BOUNCE_MS = 800
/** Below this window width the page takes its phone layout (cards centred, two a row). */
const NARROW_PX = 640

type ChipId = 'all' | 'mine' | 'shared' | 'links'
const CHIPS: readonly ChipId[] = ['all', 'mine', 'shared', 'links']
// The card collection's own keys: it starts everyone on All · A→Z (the old tabs' choice isn't carried over).
const CHIP_KEY = 'papan.pdapps.show'
const SORT_KEY = 'papan.pdapps.sortBy'

/** A→Z by name — how search results read. */
const byName = (a: TileView, b: TileView) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** The browser history entry's index (react-router keeps it in `history.state`; a replace keeps it). */
const historyIndex = (): number | null => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx
  // (A page that replaced history.state without one leaves NaN in later entries.)
  return Number.isInteger(idx) ? (idx as number) : null
}

/** Is this the Recent page (`…/apps/recent`)? It lives inside the same window. */
const RECENT_PAGE_RE = /\/apps\/recent\/?$/

export interface AppsViewProps {
  urlPrefix: string
  /** The active tenant — decides which entries run here and which switch org. */
  tenantId: string
  isOrg: boolean
  orgEmail?: string | null
  /** The active org's name, for its section heading. */
  orgName?: string | null
  /** Is this window on screen? The shell keeps hidden windows mounted. */
  deckActive?: boolean
  /**
   * An identity-only tenant has no drive: the window shows only the person's own
   * Recent and Favorites (which follow the account, so they hold what they opened
   * as their other identities) — no org part, no Share with people, no "Manage in Drive".
   */
  historyOnly?: boolean
  /**
   * Run an app of ANOTHER org whose entry was judged as one of the person's several
   * identities there, none chosen yet this session: the shell's tenant hook makes
   * that identity the org's choice ONLY IF STILL UNSET (a choice made since the list
   * was read is never undone), then the app loads fresh as whatever is chosen.
   * Absent: the app just opens (the org then picks the identity itself).
   */
  chooseIdentityIfUnset?: (tenantId: string, email: string) => Promise<boolean>
  /** The drive's destination picker, for More → Copy to / Move to (as Drive → PDApps gets it). Absent: neither is offered. */
  FolderPickerDialog?: FolderPicker
  /** More → Edit app opens the app's settings face (as Drive → PDApps does). */
  onRequestMiniappView?: (folderId: string, view: 'settings') => void
}

/** What a card stands for — the three sources, one card component. */
type Tile =
  | { type: 'entry'; entry: PdappEntry }
  | { type: 'org'; app: ProjectItem }

interface TileView extends CardView {
  tile: Tile
  /** The app itself across sources (a favorite, a history entry and the org's row of
   *  one app share it) — de-duplication, "New" and "last opened" key on it. */
  key: string
  openedAt?: string | null
  createdAt?: string | null
  /** For an app of the active org's drive: Yours or Shared with me. */
  group?: 'mine' | 'shared'
}

/**
 * How many columns a card grid has right now — so Recent can fill exactly one row. A
 * callback ref, since the grid mounts late; `auto-fill` keeps its empty tracks, so the
 * count doesn't depend on how many cards are in it.
 */
function useGridColumns(initial: number, cardW: number): [(el: HTMLDivElement | null) => void, number] {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [cols, setCols] = useState(initial)
  useEffect(() => {
    if (!el) return
    const measure = () => {
      if (!el.clientWidth) return // hidden: its tracks aren't resolved
      const n = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length
      if (n > 0) setCols(n)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
    // A new card size changes the tracks without resizing the grid's own box.
  }, [el, cardW])
  return [setEl, cols]
}

/**
 * "N PDApps collected · M favorites" — the N counts up once. Its own component, so each
 * frame of the count re-renders this line only, never the window's cards.
 */
function CollectionCounter({ count, favorites, ready, played }: {
  count: number; favorites: number; ready: boolean
  /** The window's own "already counted" flag: the line remounts (the Recent page and back) without counting again. */
  played: { current: boolean }
}) {
  const { t } = useTranslation('files')
  const shown = useCountUp(count, ready, played)
  return (
    <p className="text-xs themed-text-muted leading-tight mt-0.5 truncate" data-testid="apps-counter">
      {/* The count-up is for the eye; a screen reader hears the final value. */}
      <span aria-hidden>
        {t('appsWindow.counter.collected', { count: shown })}
        {favorites > 0 && <> · {t('appsWindow.counter.favorites', { count: favorites })}</>}
      </span>
      <span className="sr-only">
        {t('appsWindow.counter.collected', { count })}
        {favorites > 0 && <> · {t('appsWindow.counter.favorites', { count: favorites })}</>}
      </span>
    </p>
  )
}

/** Count up to `target` once the page has everything (reduced motion: straight there). */
function useCountUp(target: number, ready: boolean, played: { current: boolean }): number {
  const [n, setN] = useState(() => (played.current ? target : 0))
  useEffect(() => {
    if (!ready) return
    if (played.current || reducedMotion()) { setN(target); return }
    played.current = true
    const start = performance.now()
    let raf = requestAnimationFrame(function step(now) {
      const p = Math.min(1, (now - start) / 700)
      setN(Math.round(target * (1 - (1 - p) ** 3)))
      if (p < 1) raf = requestAnimationFrame(step)
    })
    return () => { cancelAnimationFrame(raf); setN(target) }
  }, [target, ready])
  return n
}

export function AppsView({ urlPrefix, tenantId, isOrg, orgEmail, orgName, deckActive = true, historyOnly = false, chooseIdentityIfUnset, FolderPickerDialog, onRequestMiniappView }: AppsViewProps) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const toast = useToast()
  const onRecentPage = RECENT_PAGE_RE.test(location.pathname)
  usePageTitle(onRecentPage ? t('appsWindow.recentPage.title') : t('appsWindow.title'), deckActive)

  const projects = useProjects(!historyOnly)
  const incoming = useIncomingSharedProjects(!historyOnly)
  // The shell's own lists run only while the window is SHOWN: a kept-alive hidden
  // window must not re-read them on every app switch elsewhere (each read judges
  // every entry server-side). Shown again, a list that went stale meanwhile reads.
  const history = usePdappHistory(deckActive)
  // One heavy list at a time: each judges its entries server-side (a chain of
  // queries per link), so "Shared with me" starts once the history has answered.
  const sharedLinks = useSharedLinkApps(false, deckActive && !historyOnly && history.isFetched)
  // The drive's lists: looking at the window again is the "look again".
  useRefreshWhenShown(() => { if (!historyOnly) { void projects.refetch(); void incoming.refetch() } })

  // "Show more" pages of the history, appended below the first — on the Recent page, the
  // only place they show (leaving it empties them). When the first page changes what it
  // holds (a pin, a new open, a removal), the pages already shown are read AGAIN from its
  // new end, as far as they went: the person keeps their place, nothing falls into a gap
  // between the pages, and every entry is still the server's answer (one judged out since
  // is gone). Meanwhile they show without what the first page or the pins hold, and never
  // an entry removed here. Clear history, another identity or leaving the page empties them.
  const [morePages, setMorePages] = useState<{ entries: PdappEntry[]; next: string | null } | null>(null)
  const morePagesRef = useRef(morePages)
  morePagesRef.current = morePages
  const [loadingMore, setLoadingMore] = useState(false)
  const [rereading, setRereading] = useState(false)
  // Each answer belongs to the pages it was asked for: a reset or a newer re-read makes it stale.
  const moreGen = useRef(0)
  // Entries removed here: an answer already on its way when they were removed never brings them back.
  const removedKeys = useRef(new Set<string>())
  const [rereadTick, setRereadTick] = useState(0)
  // (Running an app of ANOTHER org changes the tenant, and the shell remounts this window
  // for it — the loaded pages go with it, as everything a window holds does on an org switch.)
  const resetMore = () => {
    moreGen.current++
    morePagesRef.current = null
    removedKeys.current = new Set()
    setRereading(false)
    setMorePages(null)
  }
  const dropMore = (key: string) => {
    removedKeys.current.add(key)
    setMorePages(m => (m ? { ...m, entries: m.entries.filter(e => e.key !== key) } : m))
  }
  useEffect(() => { resetMore() }, [tenantId, orgEmail])
  // Only when the person leaves the Recent page in the shown window: an app run from it
  // hides the window (another address) — Back to it must find the pages as they were.
  useEffect(() => { if (deckActive && !onRecentPage) resetMore() }, [deckActive, onRecentPage])
  // What the first page HOLDS (its entries and pins as sets, and where it ends) — a
  // reorder (the deck arranged, an app reopened) changes nothing below it.
  const firstPageShape = useMemo(() => {
    const d = history.data
    if (!d) return null
    return [d.next ?? '', d.recent.map(e => e.key).sort().join(','), d.pinned.map(e => e.key).sort().join(',')].join('|')
  }, [history.data])
  const shapeRef = useRef(firstPageShape)
  shapeRef.current = firstPageShape
  const historyRef = useRef(history.data)
  historyRef.current = history.data
  useEffect(() => {
    const loaded = morePagesRef.current
    const first = historyRef.current
    if (!loaded || !first || firstPageShape === null) return
    const gen = ++moreGen.current
    const want = loaded.entries.length
    setRereading(true)
    void (async () => {
      const entries: PdappEntry[] = []
      let next = first.next
      while (next && entries.length < want) {
        const page = await fetchHistoryPage(next)
        if (gen !== moreGen.current) return
        entries.push(...page.recent)
        next = page.next
      }
      if (gen === moreGen.current) setMorePages({ entries: entries.filter(e => !removedKeys.current.has(e.key)), next })
    })()
      .catch(() => { /* the shown pages stay as they were; the next change reads them again */ })
      .finally(() => { if (gen === moreGen.current) setRereading(false) })
  }, [firstPageShape, rereadTick])
  const moreEntries = useMemo(() => {
    if (!morePages) return []
    // Recent holds favorites too, so only the first page's own entries count as shown.
    const shown = new Set((history.data?.recent ?? []).map(e => e.key))
    const out: PdappEntry[] = []
    for (const e of morePages.entries) {
      if (shown.has(e.key) || removedKeys.current.has(e.key)) continue // on the first page, removed — or twice
      shown.add(e.key)
      out.push(e)
    }
    return out
  }, [morePages, history.data])
  const nextCursor = morePages ? morePages.next : history.data?.next ?? null
  const showMore = async () => {
    // Not while the shown pages are being read again (its cursor is about to move).
    if (!nextCursor || loadingMore || rereading) return
    setLoadingMore(true)
    const gen = moreGen.current
    const shape = shapeRef.current
    try {
      const page = await fetchHistoryPage(nextCursor)
      if (gen !== moreGen.current) return
      setMorePages(m => ({ entries: [...(m?.entries ?? []), ...page.recent.filter(e => !removedKeys.current.has(e.key))], next: page.next }))
      // The first page changed while this page was on its way: its cursor was the old end — read again.
      if (shapeRef.current !== shape) setRereadTick(n => n + 1)
    } catch { toast.error(t('appsWindow.error')) } finally { setLoadingMore(false) }
  }

  // ── The page's own width: the phone layout follows the WINDOW, not the screen ──
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(1024)
  useEffect(() => {
    if (!rootEl) return
    // Hidden (the window kept alive under display:none) measures 0: keep the last real width.
    const measure = () => { const w = rootEl.clientWidth; if (w > 0) setWidth(w) }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(rootEl)
    return () => ro.disconnect()
  }, [rootEl])
  const narrow = width < NARROW_PX
  // The sticky top bar's height (it wraps to two rows on a phone): what the scroller keeps clear.
  const [topbarEl, setTopbarEl] = useState<HTMLDivElement | null>(null)
  const [topbarH, setTopbarH] = useState(64)
  useEffect(() => {
    if (!topbarEl) return
    const measure = () => { const h = topbarEl.offsetHeight; if (h > 0) setTopbarH(h) }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(topbarEl)
    return () => ro.disconnect()
  }, [topbarEl])

  // ── The person's choices (this browser only) ──
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const [chip, setChipState] = useState<ChipId>(() => readPref(CHIP_KEY, CHIPS) ?? 'all')
  const setChip = (c: ChipId) => { setChipState(c); writePref(CHIP_KEY, c) }
  const [sort, setSortState] = useState<CollectionSort>(() => readPref(SORT_KEY, COLLECTION_SORTS) ?? 'name')
  const setSort = (s: CollectionSort) => { setSortState(s); writePref(SORT_KEY, s) }
  // Every card is one size — a collection reads as a set. A phone fits three a row (the
  // width less its 16 px gutters and the 10 px gaps); a screen too small for three
  // readable cards (under 88 px each) fits two — never wider than the page.
  const narrowCols = Math.floor((width - 32 - 20) / 3) >= 88 ? 3 : 2
  const cardW = narrow ? Math.max(1, Math.floor((width - 32 - 10 * (narrowCols - 1)) / narrowCols)) : CARD_W
  const [recentGridRef, recentCols] = useGridColumns(8, cardW)
  // The Recent page's chip — in the address, so Back and a reload keep it.
  const filterParam = params.get('filter')
  const recentFilter: RecentFilter = (RECENT_FILTERS as readonly string[]).includes(filterParam ?? '') ? filterParam as RecentFilter : 'all'
  // (Keeps the entry's state: the ← knows the page came from "View all".)
  const setRecentFilter = (f: RecentFilter) => setParams((p) => { const n = new URLSearchParams(p); if (f === 'all') n.delete('filter'); else n.set('filter', f); return n }, { replace: true, state: location.state })

  const allProjects = useMemo(() => [...(projects.data ?? []), ...(incoming.data ?? [])], [projects.data, incoming.data])
  const orgApps = useMemo(() => buildOrgApps(allProjects, { orgEmail, isOrg, query }), [allProjects, orgEmail, isOrg, query])
  // The whole collection, unfiltered — what "New" and the counter count.
  const orgAppsAll = useMemo(() => buildOrgApps(allProjects, { orgEmail, isOrg }), [allProjects, orgEmail, isOrg])
  const pinned = useMemo(() => filterEntries(history.data?.pinned ?? [], query), [history.data, query])
  const recent = useMemo(() => filterEntries([...(history.data?.recent ?? []), ...moreEntries], query), [history.data, moreEntries, query])
  const linkShared = useMemo(() => filterEntries(sharedLinks.data ?? [], query), [sharedLinks.data, query])

  /** Is this org app pinned already? Its pin entry, to unpin by. */
  const pinOf = useCallback((appId: string) => (history.data?.pinned ?? [])
    .find(e => e.kind === 'app' && e.app?.appId === appId && e.app.tenantId === tenantId) ?? null, [history.data, tenantId])

  /** The app across sources: an app of this org is ONE key whichever list it came from. */
  const keyOfEntry = useCallback((e: PdappEntry) => {
    if (e.kind === 'app' && e.app) return e.app.tenantId === tenantId ? `org:${e.app.appId}` : `app:${e.app.tenantId}:${e.app.appId}`
    // A link entry carries no app id; two same-named link apps are two apps. A link app both
    // in history and shared with you may show twice — never one hiding another.
    return `${e.source}:${e.key}`
  }, [tenantId])
  /** When the person last opened each app — from their history (the Collection's "Recently opened"). */
  const openedByKey = useMemo(() => {
    const m = new Map<string, string>()
    for (const e of [...(history.data?.recent ?? []), ...(history.data?.pinned ?? []), ...moreEntries]) {
      if (!e.openedAt) continue
      const k = keyOfEntry(e)
      const prev = m.get(k)
      if (!prev || prev < e.openedAt) m.set(k, e.openedAt)
    }
    return m
  }, [history.data, moreEntries, keyOfEntry])
  const groupOf = useCallback((appId: string): 'mine' | 'shared' | undefined => (
    orgAppsAll.mine.some(p => p.folderId === appId) ? 'mine' : orgAppsAll.shared.some(p => p.folderId === appId) ? 'shared' : undefined
  ), [orgAppsAll])

  // ── Actions ─────────────────────────────────────────────────────────────
  /** The URL prefix of the org an entry belongs to (this one's, or another's). */
  const prefixOf = useCallback((where?: { tenantId: string; personal: boolean }) => (
    !where || where.tenantId === tenantId ? urlPrefix : where.personal ? '/~' : `/t/${where.tenantId}`
  ), [tenantId, urlPrefix])
  /**
   * Go to `path` in the org of `where` — as the identity it was listed for, when that
   * org would otherwise pick one itself (`switchTo`). `replace`: in place of the current
   * history entry (the flipped card's own, so Back from the app lands on this page).
   */
  const goTo = useCallback((path: string, where?: { tenantId: string; personal: boolean; switchTo?: string }, replace = false) => {
    const elsewhere = !!where && where.tenantId !== tenantId
    if (elsewhere && where!.switchTo && chooseIdentityIfUnset) {
      void chooseIdentityIfUnset(where!.tenantId, where!.switchTo).then((ok) => {
        if (ok) window.location.assign(path)
        else toast.error(t('appsWindow.actionFailed'))
      })
      return
    }
    navigate(path, { replace })
  }, [navigate, tenantId, toast, t, chooseIdentityIfUnset])
  /** Does running this go through the router (not a new tab, not a fresh page load)? */
  const runsInRouter = useCallback((tile: Tile) => {
    if (tile.type === 'org') return true
    const e = tile.entry
    if (e.kind !== 'app' || !e.app) return false
    return !(e.app.tenantId !== tenantId && e.app.switchTo && chooseIdentityIfUnset)
  }, [tenantId, chooseIdentityIfUnset])
  const run = useCallback((tile: Tile, replace = false) => {
    if (tile.type === 'org') return goTo(`${urlPrefix}/app/${tile.app.folderId}`, undefined, replace)
    const e = tile.entry
    if (e.kind === 'app' && e.app) return goTo(`${prefixOf(e.app)}/app/${e.app.appId}`, e.app, replace)
    openLinkApp(qc, e).catch(() => toast.error(t('appsWindow.linkUnavailable')))
  }, [goTo, urlPrefix, prefixOf, qc, toast, t])

  // ── Launch: the card bounces twice, then the app opens ─────────────────
  // A window switch in one frame reads as a jump; like the macOS Dock, the clicked card
  // bounces twice first, so the click visibly "starts" the app. The app opens as the
  // second bounce lands.
  /** The card that is starting — its PLACE (section + id): an app in Recent and Favorites is two cards. */
  const [bouncing, setBouncing] = useState<string | null>(null)
  const [openedNow, setOpenedNow] = useState<ReadonlySet<string>>(() => new Set())
  const launchTimer = useRef<number | null>(null)
  const locationRef = useRef(location)
  locationRef.current = location
  const cancelLaunch = () => {
    if (launchTimer.current !== null) { window.clearTimeout(launchTimer.current); launchTimer.current = null }
    setBouncing(null)
  }
  // Leaving the window (or the page it was started on) drops a launch still bouncing.
  useEffect(() => () => { if (launchTimer.current !== null) window.clearTimeout(launchTimer.current) }, [])
  useEffect(() => { if (!deckActive) cancelLaunch() }, [deckActive])
  /**
   * Run a card's app, after the bounce. A LINK app opens a new browser tab, which must
   * happen inside the click (a delayed one is a blocked pop-up) — it just opens; so does
   * every app when reduced motion is asked for.
   */
  const launch = (view: TileView, place: string, replace = false) => {
    if (bouncing) return
    setOpenedNow(s => new Set(s).add(view.key))
    const isLink = view.tile.type === 'entry' && view.tile.entry.kind === 'link'
    // No card drawn there (its app moved section meanwhile): nothing to bounce — open now.
    const drawn = !!rootEl?.querySelector(`.pdc[data-place="${CSS.escape(place)}"]`)
    if (isLink || reducedMotion() || !drawn) { run(view.tile, replace); return }
    setDealing(false)
    setBouncing(place)
    // Where it was started: the page, and — for a launch that REPLACES the current entry —
    // that entry (its index: a `replace` keeps it, Back / Forward change it), so it never
    // takes over an entry the person has since moved to. A launch that adds an entry needs
    // only the page (a card closing may step back over its own entry meanwhile). Where the
    // index can't be counted (a page left history.state without one), the page alone decides.
    const at = { path: location.pathname, idx: historyIndex() }
    launchTimer.current = window.setTimeout(() => {
      launchTimer.current = null
      setBouncing(null)
      const moved = replace && at.idx !== null && historyIndex() !== at.idx
      if (locationRef.current.pathname !== at.path || moved) return
      run(view.tile, replace)
    }, LAUNCH_BOUNCE_MS)
  }

  // #538 — opened from the identity bar's "Made by": show the PDApp in its folder, if it's in my drive.
  useEffect(() => {
    const token = takeLocateFragment()
    if (!token) return
    locateLinkApp(token)
      .then((path) => { if (path) navigate(path); else toast.info(t('appsWindow.notInDrive')) })
      .catch(() => {})
  }, [navigate, toast, t])
  /**
   * A card about to leave the page (Remove): once it has, focus goes to its neighbour in
   * the same row (next, else previous), else the page's first card — never to the document.
   */
  const focusAfterRemoval = (src: HTMLElement | null) => {
    const card = src?.closest<HTMLElement>('[data-card-id]') ?? null
    const all = card?.parentElement ? Array.from(card.parentElement.children) as HTMLElement[] : []
    const at = card ? all.indexOf(card) : -1
    const near = [...all.slice(at + 1), ...all.slice(0, Math.max(0, at)).reverse()].filter(c => c.matches('[data-card-id]'))
    return () => {
      if (!src) return
      let frames = 0
      const settle = () => {
        // The list reads again after the removal: wait (≤ ~3 s) for the card to go.
        if (src.isConnected) { if (frames++ < 180) requestAnimationFrame(settle); return }
        const lost = !document.activeElement || document.activeElement === document.body
        if (!lost) return
        const to = near.find(c => c.isConnected)?.querySelector<HTMLElement>('.pdc-hit') ?? rootEl?.querySelector<HTMLElement>('.pdc .pdc-hit')
        to?.focus({ preventScroll: true })
      }
      settle()
    }
  }
  const guarded = (fn: () => Promise<void>) => { fn().catch(() => toast.error(t('appsWindow.actionFailed'))) }

  // ── Share ───────────────────────────────────────────────────────────────
  /** The full Share dialog (links with their own audience) — for an app of THIS org's drive. */
  const [fullShare, setFullShare] = useState<{ id: string; name: string; path: string; isFolder: boolean } | null>(null)
  /** The small Share modal, for any card ("Share & access" for an app of this org's drive). */
  const [quickShare, setQuickShare] = useState<TileView | null>(null)
  /** The drive's Who has access dialog, opened from Share & access — an app of THIS org's drive. */
  const [accessFor, setAccessFor] = useState<{ id: string; name: string; path: string } | null>(null)
  /** More: the drive's own app menu and its dialogs (the same hook Drive → PDApps uses). */
  // A change made there (a rename, a delete, a new icon, a move) reaches Recent and Favorites too:
  // their cards come from the history, which the drive's own refresh doesn't touch.
  const { handlers: cardActions, dialogs: cardDialogs, open: cardDialogOpen } = useProjectCardActions({ isOrg, FolderPickerDialog, onChanged: () => reloadHistory(qc) })

  /**
   * The app's real drive row in THIS org, when it has one — what the full Share dialog
   * acts on (the active org, the real name and path). Never for a link app or an app of
   * another org.
   */
  const driveRowFor = (tile: Tile): { id: string; name: string; path: string; isFolder: boolean } | null => {
    if (tile.type === 'org') return { id: tile.app.folderId, name: tile.app.name, path: tile.app.path, isFolder: !isSharedPointer(tile.app.name) }
    const e = tile.entry
    if (e.kind !== 'app' || !e.app || e.app.tenantId !== tenantId) return null
    const appId = e.app.appId
    const row = allProjects.find(p => p.folderId === appId)
    return row ? { id: appId, name: row.name, path: row.path, isFolder: !e.app.mounted } : null
  }
  /** Share & access: an app whose access is managed in THIS org's drive (the drive's own row). */
  const accessRowFor = (tile: Tile) => (historyOnly ? null : driveRowFor(tile))
  const shareTargetFor = (tile: Tile): AppShareTarget => {
    const here = isOrg ? orgName ?? null : null
    // A Personal app's address (`/~/app/…`) opens the OPENER's own Personal space — it is
    // nobody else's way in, so it is never offered to pass on.
    const address = (prefix: string, appId: string) => (prefix === '/~' ? null : `${window.location.origin}${prefix}/app/${appId}`)
    if (tile.type === 'org') {
      return { kind: 'own', fileId: tile.app.folderId, papanUrl: address(urlPrefix, tile.app.folderId), canShare: !historyOnly, orgName: here }
    }
    const e = tile.entry
    if (e.kind === 'link' || !e.app) return { kind: 'link', entry: e }
    const papanUrl = address(prefixOf(e.app), e.app.appId)
    if (e.app.tenantId !== tenantId) return { kind: 'elsewhere', papanUrl, orgName: e.orgName, personal: e.app.personal }
    return { kind: 'own', fileId: e.app.appId, papanUrl, canShare: !historyOnly && !!driveRowFor(tile), orgName: here }
  }

  // "Share from ‹Org›…" lands here as `?share=<appId>`: open the full Share dialog on that
  // app once this org's drive lists have answered — or say it can't be shared from here.
  const shareParam = params.get('share')
  useEffect(() => {
    if (!shareParam || !deckActive || historyOnly) return
    if (projects.isLoading || incoming.isLoading) return
    const row = allProjects.find(p => p.folderId === shareParam)
    setParams((p) => { const n = new URLSearchParams(p); n.delete('share'); return n }, { replace: true, state: location.state })
    if (row) setFullShare({ id: row.folderId, name: row.name, path: row.path, isFolder: !isSharedPointer(row.name) })
    else toast.info(t('appsWindow.shareDialog.notHere'))
  }, [shareParam, deckActive, historyOnly, projects.isLoading, incoming.isLoading, allProjects, setParams, toast, t])

  const onClear = async () => {
    const ok = await confirm({ title: t('appsWindow.clear.title'), message: t('appsWindow.clear.message'), confirmText: t('appsWindow.clear.confirm'), type: 'danger' })
    if (ok) guarded(() => clearHistory(qc).then(resetMore))
  }

  // ── Cards ───────────────────────────────────────────────────────────────
  const personalLabel = t('appsWindow.sections.personal')
  // The org the window's own section belongs to.
  const orgLabel = !isOrg ? personalLabel : orgName || null
  /**
   * Where an entry is from, in words — a public link, a link (and its org), or the
   * org / Personal space it belongs to when that isn't where the person is now. An app
   * of the org they're in says nothing: that's the default, and naming it on every
   * card would only push the time off the line.
   */
  const sourceOf = (e: PdappEntry): string | null => {
    if (e.kind === 'link') return e.audience === 'public' ? t('appsWindow.publicLink') : e.orgName ? t('appsWindow.namedLink', { org: e.orgName }) : t('appsWindow.viaLink')
    if (!e.app || e.app.tenantId === tenantId) return null
    return e.app.personal ? personalLabel : e.orgName
  }
  /**
   * What kind of app an entry is, and that in words: a link of the org the person is in
   * now (internal), any other link (external — another org's, a Personal space's, or
   * public), else an app of a Personal space or of an org.
   */
  const entryKind = (e: PdappEntry): { kind: CardKind; kindLabel: string } => {
    if (e.kind === 'link') {
      if (e.fromHere) return { kind: 'link', kindLabel: t('appsWindow.kind.internal', { org: e.orgName ?? orgLabel ?? '' }) }
      if (e.audience === 'public') return { kind: 'public', kindLabel: t('appsWindow.publicLink') }
      return { kind: 'external', kindLabel: e.orgName ? t('appsWindow.kind.external', { org: e.orgName }) : t('appsWindow.kind.externalNoOrg') }
    }
    const here = !e.app || e.app.tenantId === tenantId
    // An app shared in from another org runs here, through its `.shared` row — but it is THAT
    // org's app (as its collection card says): the source org the drive row names.
    const sharedIn = here ? mountedSource(e) : null
    if (sharedIn) return { kind: 'org', kindLabel: sharedIn }
    const personal = here ? !isOrg : !!e.app?.personal
    return personal
      ? { kind: 'personal', kindLabel: personalLabel }
      : { kind: 'org', kindLabel: (here ? orgLabel : e.orgName) ?? '' }
  }
  /** An entry of an app shared into this drive from another org: that org's name (else null). */
  const mountedSource = (e: PdappEntry): string | null => {
    if (e.kind !== 'app' || !e.app?.mounted) return null
    const row = allProjects.find(p => p.folderId === e.app!.appId)
    return row && isSharedPointer(row.name) ? row.owner?.name ?? null : null
  }
  /**
   * The card's text line: when it was last opened — or, in the collection, that it
   * hasn't been yet (`orNever`).
   */
  const openedLine = (openedAt: string | null, orNever: boolean): string | undefined =>
    openedAt ? t('appsWindow.card.opened', { when: formatRelativeDate(openedAt) }) : orNever ? t('appsWindow.card.never') : undefined
  /**
   * `show`: Recent and Favorites say when it was last opened; the Collection also says
   * when it hasn't been.
   */
  const entryView = (e: PdappEntry, show: 'when-and-where' | 'where'): TileView => {
    const from = sourceOf(e)
    const elsewhere = e.kind === 'app' && !!e.app && e.app.tenantId !== tenantId
    const key = keyOfEntry(e)
    return {
      id: `${e.source}:${e.key}`,
      key,
      name: e.name,
      icon: e.icon,
      iconRef: e.kind === 'app' && e.app && e.app.tenantId === tenantId && !e.app.mounted ? e.app.appId : null,
      hint: from ?? undefined,
      // The banner says where it's from (this org, when it's here); the text line, when it was opened.
      subtitle: openedLine(e.openedAt ?? openedByKey.get(key) ?? null, show === 'where'),
      ...entryKind(e),
      org: elsewhere ? { name: e.app!.personal ? personalLabel : e.orgName ?? '', personal: !!e.app!.personal } : undefined,
      pinned: e.pinned,
      openedAt: e.openedAt ?? openedByKey.get(key) ?? null,
      group: e.kind === 'app' && e.app && e.app.tenantId === tenantId ? groupOf(e.app.appId) : undefined,
      colors: cardColors(e.icon, e.name),
      tile: { type: 'entry', entry: e },
    }
  }
  /** `from` (Shared with me): who shared it — its maker in this org, or the org that shared it in. */
  const orgView = (p: ProjectItem, group: 'mine' | 'shared', from?: string | null): TileView => {
    const key = `org:${p.folderId}`
    const name = appName(p)
    return {
      id: key,
      key,
      name,
      icon: p.metadataJson ?? null,
      // A `.shared` mount's composited PNG isn't reachable from this tenant: draw the spec.
      iconRef: isSharedPointer(p.name) ? null : p.folderId,
      hint: from ?? undefined,
      subtitle: openedLine(openedByKey.get(key) ?? null, true),
      // Another org's app, shared in: it runs here, but wears the org it comes from.
      org: isSharedPointer(p.name) && from ? { name: from, personal: false } : undefined,
      // An app of this drive: of a Personal space or of this org — one shared in, of the org it comes from.
      ...(isSharedPointer(p.name) && from
        ? { kind: 'org' as const, kindLabel: from }
        : isOrg ? { kind: 'org' as const, kindLabel: orgLabel ?? '' } : { kind: 'personal' as const, kindLabel: personalLabel }),
      pinned: !!pinOf(p.folderId),
      openedAt: openedByKey.get(key) ?? null,
      createdAt: p.createdAt ?? null,
      group,
      colors: cardColors(p.metadataJson ?? null, name),
      tile: { type: 'org', app: p },
    }
  }

  const loading = history.isLoading || (!historyOnly && (projects.isLoading || incoming.isLoading))
  // Everything the page will show has answered (the links come last) — "New" and the counter wait for it.
  const allLoaded = !loading && (historyOnly || sharedLinks.isFetched || sharedLinks.isError)

  const pinnedViews = pinned.map(e => entryView(e, 'when-and-where'))
  const recentViews = recent.map(e => entryView(e, 'when-and-where'))
  const mineViews = orgApps.mine.map(p => orgView(p, 'mine'))
  const sharedViews = orgApps.shared.map(p => orgView(p, 'shared', p.owner?.name))
  const linkViews = linkShared.map(e => entryView(e, 'where'))

  // ── "New to you": apps in the collection this browser hasn't shown before ──
  // A visit starts when the window is shown; its ribbons stay for that visit and the
  // apps count as seen when it ends. The first visit marks everything seen at once.
  const collectionKeys = useMemo(() => [
    ...orgAppsAll.mine.map(p => `org:${p.folderId}`),
    ...orgAppsAll.shared.map(p => `org:${p.folderId}`),
    ...(sharedLinks.data ?? []).map(keyOfEntry),
  ], [orgAppsAll, sharedLinks.data, keyOfEntry])
  // A shared card stands for every link naming the person for that app (the server shows
  // the one in use): the memory remembers them all, and the card is "New" only if none was seen.
  const linkAliases = useMemo(() => new Map((sharedLinks.data ?? [])
    .filter(e => e.alsoKeys?.length)
    .map(e => [keyOfEntry(e), e.alsoKeys!.map(k => `shared:${k}`)] as const)), [sharedLinks.data, keyOfEntry])
  const seenKeys = useMemo(() => [...collectionKeys, ...[...linkAliases.values()].flat()], [collectionKeys, linkAliases])
  const keysRef = useRef(seenKeys)
  keysRef.current = seenKeys
  // undefined: this visit hasn't read the memory yet; null: the first visit ever.
  const [visitSeen, setVisitSeen] = useState<ReadonlySet<string> | null | undefined>(undefined)
  const visitReady = useRef(false)
  useEffect(() => { if (deckActive) { setVisitSeen(undefined); visitReady.current = false } }, [deckActive, tenantId])
  useEffect(() => {
    if (!deckActive || historyOnly || visitSeen !== undefined || !allLoaded) return
    const stored = readSeen(tenantId)
    if (!stored) writeSeen(tenantId, rememberSeen(null, keysRef.current))
    setVisitSeen(stored ? new Set(stored) : null)
    visitReady.current = true
  }, [deckActive, historyOnly, visitSeen, allLoaded, tenantId])
  useEffect(() => {
    if (!deckActive || historyOnly) return
    const tid = tenantId
    return () => { if (visitReady.current) writeSeen(tid, rememberSeen(readSeen(tid), keysRef.current)) }
  }, [deckActive, historyOnly, tenantId])
  const newSet = useMemo(() => {
    if (visitSeen === undefined) return new Set<string>()
    const unseen = newCards(seenKeys, visitSeen)
    return new Set(collectionKeys.filter(k => unseen.has(k) && (linkAliases.get(k) ?? []).every(a => unseen.has(a))))
  }, [collectionKeys, seenKeys, linkAliases, visitSeen])
  const withNew = (v: TileView): TileView => (newSet.has(v.key) && !openedNow.has(v.key) ? { ...v, isNew: true } : v)

  // ── The counter: every app the person can get back to, each once — except a link that
  //    names the person AND is in their history: the history row carries no share id to
  //    match (kept that way: an app learns no ids), so it counts twice. ──
  const counterPlayed = useRef(false)
  const collectedCount = useMemo(() => {
    const keys = new Set<string>(collectionKeys)
    for (const e of [...(history.data?.recent ?? []), ...(history.data?.pinned ?? [])]) keys.add(keyOfEntry(e))
    return keys.size
  }, [collectionKeys, history.data, keyOfEntry])
  const favCount = history.data?.pinned.length ?? 0

  // More history the server has not judged yet keeps the list from reading as empty.
  const moreHistory = !!nextCursor && !query.trim()
  const nothing = pinnedViews.length + recentViews.length + sharedViews.length + mineViews.length + linkViews.length === 0 && !moreHistory

  // ── The page's light takes the colours of the card under the pointer / focus ──
  const ambRef = useRef<HTMLDivElement>(null)
  const lightUp = useCallback((c: { from: string; to: string }) => {
    const el = ambRef.current
    if (!el) return
    el.style.setProperty('--amb1', c.from)
    el.style.setProperty('--amb2', c.to)
  }, [])
  const firstColors = (recentViews[0] ?? pinnedViews[0])?.colors
  useEffect(() => { if (firstColors) lightUp(firstColors) }, [firstColors?.from, firstColors?.to, lightUp])
  // A phone has no hover: the light follows the card nearest the middle of the screen as it scrolls.
  useEffect(() => {
    if (!rootEl || !deckActive || typeof window.matchMedia !== 'function' || !window.matchMedia('(hover: none)').matches) return
    let raf = 0
    const pick = () => {
      raf = 0
      const view = rootEl.getBoundingClientRect()
      const mid = view.top + view.height * 0.45
      let best: HTMLElement | null = null
      let bestD = Infinity
      for (const card of rootEl.querySelectorAll<HTMLElement>('.pdc[data-card-id]')) {
        const r = card.getBoundingClientRect()
        if (r.bottom < view.top || r.top > view.bottom) continue
        const d = Math.abs(r.top + r.height / 2 - mid)
        if (d < bestD) { bestD = d; best = card }
      }
      const from = best?.style.getPropertyValue('--c1')
      const to = best?.style.getPropertyValue('--c2')
      if (from && to) lightUp({ from, to })
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(pick) }
    rootEl.addEventListener('scroll', onScroll, { passive: true })
    return () => { rootEl.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [rootEl, deckActive, lightUp])

  // ── On a visit's first load, the cards are dealt in (once; not with reduced motion) ──
  const [dealing, setDealing] = useState(false)
  const dealt = useRef(false)
  useEffect(() => { if (!deckActive) dealt.current = false }, [deckActive])
  useEffect(() => {
    if (!deckActive || !allLoaded || dealt.current) return
    dealt.current = true
    if (!reducedMotion()) setDealing(true)
  }, [deckActive, allLoaded])
  useEffect(() => {
    if (!dealing) return
    const t = window.setTimeout(() => setDealing(false), 1800)
    return () => window.clearTimeout(t)
  }, [dealing])

  // ── The Favorites deck: the order the server keeps (the person's own). While it is
  //    arranged, the order on screen is `deckLive` (kept until Arrange ends). Each drop
  //    or arrow-key move shows it in the cached list at once and saves it — ONE save at a
  //    time, only the latest order queued behind it, so the server never ends on an
  //    older order than the screen. If the last save is refused, the list is read again
  //    (the server's own order) and the person is told. ──
  const [arranging, setArranging] = useState(false)
  const [announce, setAnnounce] = useState('')
  const [deckLive, setDeckLive] = useState<string[] | null>(null)
  const deckLiveRef = useRef<string[] | null>(null)
  deckLiveRef.current = deckLive
  const orderedPinned = useMemo(() => orderDeck(pinnedViews, v => v.id, deckLive), [pinnedViews, deckLive])
  // `stale`: a save landed while arranging — the list is read again ONCE, when Arrange ends.
  const deckSave = useRef<{ busy: boolean; next: string[] | null; failed: boolean; stale: boolean }>({ busy: false, next: null, failed: false, stale: false })
  const arrangingRef = useRef(false)
  const sendDeck = (keys: string[]) => {
    const q = deckSave.current
    q.busy = true
    putPinOrder(keys)
      .catch(() => { q.failed = true })
      .finally(() => {
        q.busy = false
        if (q.next) { const next = q.next; q.next = null; q.failed = false; sendDeck(next); return }
        // The last save landed: read the history again (a refetch that ran meanwhile — a pin,
        // an open — may have brought back the older order) — once, when Arrange is over.
        if (!q.failed) {
          if (arrangingRef.current) q.stale = true
          else reloadHistoryOnly(qc)
          return
        }
        // The last save was refused: the server's own order, and say so.
        q.failed = false
        q.stale = false
        setDeckLive(null)
        reloadHistory(qc)
        toast.error(t('appsWindow.deck.saveFailed'))
      })
  }
  const saveDeck = () => {
    const ids = deckLiveRef.current
    if (!ids) return
    const keyOf = new Map(pinnedViews.map(v => [v.id, v.tile.type === 'entry' ? v.tile.entry.key : null]))
    const keys = ids.map(id => keyOf.get(id)).filter((k): k is string => !!k)
    void showPinOrder(qc, keys)
    if (deckSave.current.busy) { deckSave.current.next = keys; return }
    sendDeck(keys)
  }
  useEffect(() => { if (!deckActive || onRecentPage || query.trim()) setArranging(false) }, [deckActive, onRecentPage, query])
  // Fewer than two cards (an Unfavorite, a refetch): nothing to arrange — and no Done to press.
  useEffect(() => { if (orderedPinned.length < 2) setArranging(false) }, [orderedPinned.length])
  // Arranging over: the list shows the order it now holds (shown at each move, saved behind).
  // Unmounted mid-Arrange (an org switch remounts the window): Arrange is over — a save
  // that landed meanwhile, or lands later, re-reads the history (it writes every identity's).
  useEffect(() => () => {
    arrangingRef.current = false
    const q = deckSave.current
    if (q.stale) { q.stale = false; reloadHistoryOnly(qc) }
  }, [qc])
  useEffect(() => {
    arrangingRef.current = arranging
    if (arranging) return
    setDeckLive(null)
    const q = deckSave.current
    if (q.stale && !q.busy) { q.stale = false; reloadHistoryOnly(qc) }
    // (A save still on its way reads the list itself when it lands — Arrange is over by then.)
  }, [arranging, qc])


  // ── The flipped card: lifted into the middle of the screen ─────────────
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [inspect, setInspect] = useState<{ id: string; place: string; mark: string; from: DOMRect | null; closing: boolean; quick: boolean; after?: () => void; leaves?: boolean } | null>(null)
  const inspectSource = useRef<HTMLElement | null>(null)
  // Opening pushes a history entry (no address change), so the phone's Back closes the card.
  const pushedRef = useRef(false)
  const stateCard = (location.state as { pdappsCard?: string } | null)?.pdappsCard ?? null
  // Each open marks its own entry (the card's id + a count): an entry left by an earlier
  // open (a reload, the window hidden while a card was open) never passes for this one.
  const openSeq = useRef(0)
  const openInspect = (v: TileView, place: string, el: HTMLElement) => {
    // Not while a card is starting: its Run would be dropped (one launch at a time).
    if (inspect || bouncing) return
    inspectSource.current = el
    pushedRef.current = false
    const mark = `${v.id}#${Date.now().toString(36)}${++openSeq.current}`
    setInspect({ id: v.id, place, mark, from: el.getBoundingClientRect(), closing: false, quick: false })
    const prev = (location.state && typeof location.state === 'object') ? location.state as Record<string, unknown> : {}
    navigate({ pathname: location.pathname, search: location.search }, { state: { ...prev, pdappsCard: mark } })
  }
  /**
   * Close the card. `quick`: no way back (an action that leaves). `keepEntry`: the
   * card's history entry stays for the app to take over (`replace`); otherwise it is
   * stepped back over. `after` runs once it is gone.
   */
  const closeInspect = (opts: { quick?: boolean; keepEntry?: boolean; after?: () => void; leaves?: boolean } = {}) => {
    setInspect(cur => (cur && !cur.closing ? { ...cur, closing: true, quick: !!opts.quick, after: opts.after, leaves: !!opts.leaves } : cur))
    if (pushedRef.current && !opts.keepEntry) { pushedRef.current = false; navigate(-1) }
  }
  // The card's entry arrived (opened) — or left (the phone's / browser's Back): close.
  useEffect(() => {
    if (!inspect || inspect.closing) return
    if (stateCard === inspect.mark) { pushedRef.current = true; return }
    if (pushedRef.current) {
      pushedRef.current = false
      setInspect(c => (c ? { ...c, closing: true, quick: false } : c))
    }
  }, [stateCard, inspect])
  const onInspectClosed = () => {
    const after = inspect?.after
    const leaves = inspect?.leaves
    pushedRef.current = false
    setInspect(null)
    if (!leaves) {
      // Back to the card it was opened from — or, if that card left the page (an Unfavorite
      // that dropped it), to the page's first card, never lost to the document. Before
      // `after` runs: a Share dialog it opens returns focus here when it closes.
      const src = inspectSource.current
      const to = src?.isConnected ? src : rootEl?.querySelector<HTMLElement>('.pdc .pdc-hit')
      to?.focus({ preventScroll: true })
    }
    after?.()
  }
  // An entry marked by a card that is no longer open (the page reloaded, or the window was
  // hidden, with a card open): unmark it in place, so Back isn't spent on it.
  useEffect(() => {
    if (!deckActive || !stateCard || inspect) return
    // Only the entry that is live NOW, if it still carries the mark: the router shows a new
    // address a render late (a transition), so a Run that just replaced the card's entry
    // with the app would otherwise be overwritten back to this page.
    const live = (window.history.state as { usr?: { pdappsCard?: unknown } } | null)?.usr?.pdappsCard
    if (live !== stateCard) return
    const { pdappsCard: _gone, ...rest } = (location.state ?? {}) as Record<string, unknown>
    // A leftover entry on top of the Recent page: with no countable index, its ← can't step
    // back over it to the main page — it goes there in place instead.
    if (rest.pdappsFromMain !== undefined && !Number.isInteger(rest.pdappsFromMain)) delete rest.pdappsFromMain
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: Object.keys(rest).length ? rest : null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckActive, stateCard, inspect])
  // Leaving the window (another program, another org) drops it without a trace.
  useEffect(() => { if (!deckActive) { setInspect(null); pushedRef.current = false; setFullShare(null); setQuickShare(null); setAccessFor(null) } }, [deckActive])
  // The card belongs to the page it was opened on.
  useEffect(() => { setInspect(null); pushedRef.current = false }, [onRecentPage])

  // ── On a phone, what to show and the sort are in a bottom sheet ──
  // Which sheet is open: 0 = what to show, 1 = the sort; null = none.
  const [sheetOpen, setSheetOpen] = useState<0 | 1 | null>(null)
  useEffect(() => { if (!deckActive || !narrow) setSheetOpen(null) }, [deckActive, narrow])
  const newProject = useNewProject()

  // ── The sort menu: the app's own menu, anchored to its button ──
  const [sortOpen, setSortOpen] = useState(false)
  const sortBtnRef = useRef<HTMLButtonElement | null>(null)
  const sortMenuRef = useRef<HTMLDivElement>(null)
  const closeSort = useCallback(() => setSortOpen(false), [])
  const sortPos = useAnchoredPosition({
    open: sortOpen, anchorRef: sortBtnRef, floatingRef: sortMenuRef, onDismiss: closeSort, align: 'end', exitTransitionMs: 160,
  })
  useEffect(() => {
    if (!sortOpen) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (sortMenuRef.current?.contains(target) || sortBtnRef.current?.contains(target)) return
      setSortOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setSortOpen(false); sortBtnRef.current?.focus({ preventScroll: true }) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [sortOpen])
  useEffect(() => { if (!deckActive) setSortOpen(false) }, [deckActive])
  const sortItems: FileMenuItem[] = COLLECTION_SORTS.map(o => ({
    kind: 'item', label: t(`appsWindow.sort.${o}`), icon: o === sort ? Check : undefined, testId: `apps-sort-${o}`,
    onClick: () => { setSort(o); setSortOpen(false) },
  }))

  // ── The drive's own dialogs over the page (Who has access, the full Share) ──
  // They portal to <body> and don't take focus or trap it, so while one is open the page
  // behind is made inert — no card flipped by a stray Enter, no arrow keys moving behind it —
  // and focus goes back to the card they were opened from (the card last turned over) when it
  // closes. (Not `document.activeElement` at open: the Share modal closing hands focus back
  // only after the page is already inert, so it lands on <body>.)
  const overDialog = !!accessFor || !!fullShare || cardDialogOpen
  const dialogReturn = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    if (overDialog) { dialogReturn.current = inspectSource.current; return }
    const back = dialogReturn.current
    dialogReturn.current = null
    if (back?.isConnected) back.focus({ preventScroll: true })
  }, [overDialog])

  // ── Phone tilt; the "/" search shortcut (only while this window is the one shown) ──
  const gyro = useGyroTilt(rootEl, deckActive && !inspect)
  useEffect(() => {
    if (!deckActive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || inspect || overDialog) return
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return
      // Never out of a dialog that is open over the page (Share, a confirm, a sheet, a menu).
      // (Only a SHOWN one: a dialog left mounted in a hidden kept-alive deck doesn't count.)
      if ([...document.querySelectorAll('[aria-modal="true"]')].some(m => m.getClientRects().length) || sortOpen || sheetOpen !== null) return
      e.preventDefault()
      // (No scroll: the bar is sticky — the scroller's padding would move the list.)
      searchRef.current?.focus({ preventScroll: true })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [deckActive, inspect, sortOpen, sheetOpen, overDialog])

  const cardLabels: CardLabels = {
    play: t('appsWindow.run'),
    favorite: t('appsWindow.card.favorite'),
    isNew: t('appsWindow.card.new'),
  }

  /** The back of a card: where it's from, who made it, how the person got it, when they last opened it. */
  const detailsFor = (v: TileView): CardDetail[] => {
    const here = orgLabel ?? t('appsWindow.sections.thisOrg')
    const out: CardDetail[] = []
    const tile = v.tile
    let maker: string | null | undefined
    let got: string | null = null
    const kindIcon = <KindIcon kind={v.kind} size={12} className="pdc-back-kind" />
    if (tile.type === 'org') {
      const sharedIn = isSharedPointer(tile.app.name)
      out.push({ label: t('appsWindow.card.from'), value: sharedIn && v.org ? v.org.name : here, icon: kindIcon })
      maker = v.group === 'mine' ? t('appsWindow.card.you') : sharedIn ? null : tile.app.owner?.name
      got = v.group === 'mine' ? t('appsWindow.card.gotYours') : t('appsWindow.card.gotShared')
    } else {
      const e = tile.entry
      out.push({ label: t('appsWindow.card.from'), value: (e.kind === 'link' ? e.orgName : mountedSource(e) ?? sourceOf(e)) ?? here, icon: kindIcon })
      if (e.kind === 'link') got = e.audience === 'public' ? t('appsWindow.publicLink') : t('appsWindow.card.gotNamedLink')
      else if (v.group) {
        const row = allProjects.find(p => p.folderId === e.app?.appId)
        maker = v.group === 'mine' ? t('appsWindow.card.you') : row && !isSharedPointer(row.name) ? row.owner?.name : null
        got = v.group === 'mine' ? t('appsWindow.card.gotYours') : t('appsWindow.card.gotShared')
      }
    }
    if (maker) out.push({ label: t('appsWindow.card.madeBy'), value: maker })
    if (got) out.push({ label: t('appsWindow.card.got'), value: got })
    out.push({ label: t('appsWindow.card.lastOpened'), value: v.openedAt ? formatRelativeDate(v.openedAt) : t('appsWindow.card.never') })
    return out
  }

  /** The back's star: favorite it or not, for each kind of card. */
  const favoriteFor = (v: TileView): CardFavorite => {
    const tile = v.tile
    const fav = (on: boolean, onToggle: () => void): CardFavorite => ({
      on, onToggle,
      label: on ? t('appsWindow.card.unfavorite') : t('appsWindow.card.favoriteIt'),
      testId: on ? 'apps-menu-unpin' : 'apps-menu-pin',
    })
    if (tile.type === 'org') {
      const p = tile.app
      const pin = pinOf(p.folderId)
      return fav(!!pin, () => guarded(() => (pin ? setEntryPinned(qc, pin.key, false) : pinActiveApp(qc, p.folderId))))
    }
    const e = tile.entry
    if (e.source === 'shared') return fav(e.pinned, () => guarded(() => pinSharedLink(qc, e.key, !e.pinned)))
    return fav(e.pinned, () => guarded(() => setEntryPinned(qc, e.key, !e.pinned)))
  }

  /**
   * More: the drive's own menu for the app (Drive → PDApps' ⋮), with its dialogs — for an app
   * of THIS org's drive only (an app of another org or a link app is managed where it lives).
   * Each item closes the card first; one that leaves the page lets the card go without its way back.
   */
  const projectFor = (tile: Tile): ProjectItem | null => {
    if (historyOnly) return null
    if (tile.type === 'org') return tile.app
    const a = tile.entry.app
    if (tile.entry.kind !== 'app' || !a || a.tenantId !== tenantId) return null
    return allProjects.find(p => p.folderId === a.appId) ?? null
  }
  // Leaving the page from the card takes the card's own history entry over (as Run does when the
  // app opens at once): stepping back over it first would land AFTER this push and undo it.
  const leaveTo = (path: string, before?: () => void) => closeInspect({
    quick: true, leaves: true, keepEntry: true, after: () => { before?.(); navigate(path, { replace: true }) },
  })
  const thenClose = <A extends unknown[]>(fn: (...a: A) => void) => (...a: A) => closeInspect({ after: () => fn(...a) })
  const moreHandlers = (onOpen: () => void): ProjectCardHandlers => ({
    onOpen,
    onBrowse: (p) => leaveTo(`${urlPrefix}/folder/${p.folderId}`),
    // As Drive → PDApps: the app's editor, on its Settings face.
    onEditSettings: (p) => leaveTo(`${urlPrefix}/workspace/${p.folderId}`, () => onRequestMiniappView?.(p.folderId, 'settings')),
    onShare: thenClose(cardActions.onShare),
    onShareOrg: thenClose(cardActions.onShareOrg),
    onWhoHasAccess: thenClose(cardActions.onWhoHasAccess),
    ...(cardActions.onShareInternally ? { onShareInternally: thenClose(cardActions.onShareInternally) } : {}),
    onRename: thenClose(cardActions.onRename),
    onDuplicate: thenClose(cardActions.onDuplicate),
    onDownload: thenClose(cardActions.onDownload),
    onDetails: thenClose(cardActions.onDetails),
    onDebugLog: thenClose(cardActions.onDebugLog),
    onEditIcon: thenClose(cardActions.onEditIcon),
    // Once deleted, focus goes to the card's neighbour (as Remove from history does), never to the page.
    onDelete: (p) => closeInspect({ after: () => {
      const refocus = focusAfterRemoval(inspectSource.current)
      void cardActions.onDelete(p).then(refocus)
    } }),
    ...(cardActions.onMoveTo ? { onMoveTo: thenClose(cardActions.onMoveTo) } : {}),
    ...(cardActions.onCopyTo ? { onCopyTo: thenClose(cardActions.onCopyTo) } : {}),
  })

  /** The back's actions: Run, Share (& access), More — and Remove from history on a Recent card. */
  const actionsFor = (v: TileView): CardAction[] => {
    const tile = v.tile
    const isLink = tile.type === 'entry' && tile.entry.kind === 'link'
    const runIt = () => {
      // A link app's tab must open inside this click; a router run takes the card's entry over.
      if (isLink) { run(tile); closeInspect({ quick: true }); return }
      const inRouter = runsInRouter(tile)
      const place = inspect?.place ?? v.id
      // The app takes the card's history entry over only when it opens AT ONCE (no
      // bounce: reduced motion, or its card isn't drawn). With a bounce, the card steps back
      // over its entry as it closes and the app is pushed after it — so a launch called
      // off (the window left, another page) leaves no stray entry behind.
      const instant = reducedMotion() || !rootEl?.querySelector(`.pdc[data-place="${CSS.escape(place)}"]`)
      const takeOver = inRouter && instant
      closeInspect({ quick: true, keepEntry: takeOver, leaves: true, after: () => launch(v, place, takeOver) })
    }
    const out: CardAction[] = [{
      id: 'run', primary: true, label: t('appsWindow.run'), icon: <Play size={15} />, testId: 'apps-menu-run', onClick: runIt,
    }, {
      // "Share & access" where the app's access is managed in this org's drive; a link app or
      // an app of another org offers only its link ("Share").
      id: 'share', label: accessRowFor(tile) ? t('appsWindow.shareAccess') : t('appsWindow.share'), icon: <Share2 size={14} />, testId: 'apps-menu-share',
      onClick: () => closeInspect({ after: () => setQuickShare(v) }),
    }]
    const project = projectFor(tile)
    if (project) {
      out.push({
        id: 'more', label: t('appsWindow.card.more'), icon: <MoreHorizontal size={15} />, testId: 'apps-menu-more', onClick: () => {},
        menu: () => buildProjectMenuItems(project, moreHandlers(runIt), { t, isApp: isMiniappFolder(stripPointerSuffix(project.name)), close: () => {} }),
      })
    }
    if (tile.type === 'org') return out
    const e = tile.entry
    if (e.source === 'shared') return out
    // "Remove from history" takes a favorite only out of Recent (it stays in the deck), so it
    // is offered on a favorite only where it is a RECENT card — the Recent row, the Recent page
    // ('results') — never on the deck or in the main page's search (there the card would stay,
    // as the favorite); the star is the way out there. Never on a card never opened.
    const where = (inspect?.place ?? '').split('|')[0]
    if (!e.openedAt || (e.pinned && where !== 'recent' && where !== 'results')) return out
    out.push({
      id: 'remove', danger: true, label: t('appsWindow.remove'), icon: <Trash2 size={13} />, testId: 'apps-menu-remove',
      onClick: () => closeInspect({ after: () => { const refocus = focusAfterRemoval(inspectSource.current); guarded(() => removeEntry(qc, e.key).then(() => { dropMore(e.key); refocus() })) } }),
    })
    return out
  }

  /**
   * `markNew`: the collection only — Recent and Favorites hold apps the person already
   * knows. `deal`: the card's place in the deal-in.
   */
  const renderCard = (v: TileView, opts: { where: string; markNew?: boolean; deal?: number; arranging?: boolean; placeholder?: boolean }) => {
    const place = `${opts.where}|${v.id}`
    return (
    <PdappCard
      key={v.id}
      view={opts.markNew ? withNew(v) : v}
      width={cardW}
      labels={cardLabels}
      place={place}
      bouncing={bouncing === place}
      inspecting={inspect?.place === place}
      arranging={opts.arranging}
      placeholder={opts.placeholder}
      dealIndex={opts.deal}
      onInspect={(el) => openInspect(v, place, el)}
      onPlay={opts.arranging ? undefined : () => launch(v, place)}
      onHover={() => lightUp(v.colors)}
    />
    )
  }
  const renderGrid = (where: string, views: TileView[], extra?: React.ReactNode, markNew = false, ref?: (el: HTMLDivElement | null) => void, dealFrom = 0) => (
    <div ref={ref} className={`pdc-grid${narrow ? ' is-narrow' : ''}`}>{views.map((v, i) => renderCard(v, { where, markNew, deal: dealFrom + i }))}{extra}</div>
  )

  /** The arrow keys move between the page's cards (the deck being arranged moves its own). */
  const onCardKeys = (e: React.KeyboardEvent) => {
    if (!NAV_KEYS.includes(e.key) || inspect || e.altKey || e.metaKey || e.ctrlKey || !rootEl) return
    const el = e.target as HTMLElement
    if (!el.classList?.contains('pdc-hit') || el.closest('.is-arranging-deck')) return
    const cards = Array.from(rootEl.querySelectorAll<HTMLElement>('.pdc .pdc-hit'))
    const to = nextCardIndex(cards.map(c => c.getBoundingClientRect()), cards.indexOf(el), e.key as NavKey)
    if (to < 0) return
    e.preventDefault()
    cards[to].focus({ preventScroll: true })
    cards[to].scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
  const heading = (icon: React.ReactNode, label: string, sub?: string | null, extra?: React.ReactNode) => (
    <div className="flex items-end justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="ws-home-heading flex items-center gap-1.5 min-w-0">{icon}<span className="truncate">{label}</span></h2>
        {sub && <p className="mt-0.5 text-xs themed-text-muted truncate">{sub}</p>}
      </div>
      {extra}
    </div>
  )
  const linkBtn = 'text-xs themed-text-muted hover:themed-text flex items-center gap-0.5 transition-colors shrink-0'

  /** Searching: one grid of every match, each app once (it can be a favorite AND in the collection). */
  const results = () => {
    const seen = new Set<string>()
    const all = [...recentViews, ...pinnedViews, ...mineViews, ...sharedViews, ...linkViews]
      .filter(v => { if (seen.has(v.key)) return false; seen.add(v.key); return true })
      .map(v => ({ ...v, subtitle: v.hint }))
      .sort(byName)
    return (
      <section data-testid="apps-results">
        {heading(<Search size={14} />, t('appsWindow.sections.results'), t('appsWindow.resultsCount', { count: all.length }))}
        {renderGrid('recent-page', all)}
      </section>
    )
  }

  /** The main page: Recent (one row) · Favorites (the deck) · the collection. */
  const lists = () => {
    // One row — as many as fit the window; "View all" holds the rest.
    const recentShown = historyOnly ? recentViews : recentViews.slice(0, recentCols)
    const chipViews: Record<ChipId, TileView[]> = {
      mine: mineViews,
      shared: sharedViews,
      links: linkViews,
      all: (() => { const s = new Set<string>(); return [...mineViews, ...sharedViews, ...linkViews].filter(v => (s.has(v.key) ? false : (s.add(v.key), true))) })(),
    }
    const chipIcon: Record<ChipId, React.ReactNode> = {
      all: <LayoutGrid size={13} />, mine: <User size={13} />, shared: <UsersRound size={13} />, links: <Link2 size={13} />,
    }
    const chipLabel: Record<ChipId, string> = {
      all: t('appsWindow.chips.all'), mine: t('appsWindow.sections.mine'), shared: t('appsWindow.sections.shared'), links: t('appsWindow.chips.links'),
    }
    const collection = sortCards(chipViews[chip], sort)
    const ghosts = pinnedViews.length === 0 ? 3 : 1
    const deckById = new Map(orderedPinned.map(v => [v.id, v]))
    return (
      <>
        {(recentViews.length > 0 || moreHistory) && (
          <section className="mb-10" data-testid="apps-section-recent">
            {/* Always there: the Recent page holds the whole history AND Clear history / the
                chips — a short history must still be clearable. */}
            {heading(<Clock size={14} />, t('appsWindow.sections.recent'), null, (
              <button onClick={() => navigate(`${urlPrefix}/apps/recent`, { state: { pdappsFromMain: historyIndex() ?? 'yes' } })} className={linkBtn} data-testid="apps-recent-view-all">
                {t('appsWindow.viewAll')}
                <ChevronRight size={13} />
              </button>
            ))}
            {renderGrid('recent', recentShown, undefined, false, recentGridRef, 0)}
          </section>
        )}
        <section className="mb-10" data-testid="apps-section-pinned">
          {heading(
            <Star size={14} />,
            t('appsWindow.sections.pinned'),
            arranging ? t('appsWindow.deck.hint') : t('appsWindow.deck.subtitle'),
            orderedPinned.length >= 2 ? (
              <button
                type="button"
                onClick={() => setArranging(a => !a)}
                aria-pressed={arranging}
                className={arranging ? 'themed-btn-primary rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1 shrink-0' : linkBtn}
                data-testid="apps-deck-arrange"
              >
                {arranging ? <Check size={13} /> : <Move size={13} className="mr-0.5" />}
                {arranging ? t('appsWindow.deck.done') : t('appsWindow.deck.arrange')}
              </button>
            ) : null,
          )}
          <DeckGrid
            ids={orderedPinned.map(v => v.id)}
            arranging={arranging}
            narrow={narrow}
            renderCard={(id, { placeholder, index }) => {
              const v = deckById.get(id)
              return v ? renderCard(v, { where: 'deck', arranging, placeholder, deal: 2 + index }) : null
            }}
            renderFloat={(id) => { const v = deckById.get(id); return v ? <CardFloat view={v} width={cardW} labels={cardLabels} /> : null }}
            extra={Array.from({ length: ghosts }, (_, i) => (
              <GhostCard key={`ghost-${i}`} width={cardW} text={i === 0 ? t('appsWindow.deck.ghost') : undefined} />
            ))}
            onReorder={(ids) => { deckLiveRef.current = ids; setDeckLive(ids) }}
            onMoved={(id, position, total) => {
              setAnnounce(t('appsWindow.deck.moved', { name: deckById.get(id)?.name ?? '', position, total }))
              saveDeck()
            }}
            onExit={() => setArranging(false)}
          />
        </section>
        {!historyOnly && (
          <section data-testid="apps-section-org">
            {heading(
              isOrg ? <Building2 size={14} /> : <User size={14} />,
              orgLabel ?? t('appsWindow.sections.thisOrg'),
              t('appsWindow.collection.subtitle'),
            )}
            {narrow ? (
              <div className="flex items-center gap-2 mb-5">
                {/* What's shown (with its count) and the sort, as two pills — each opens its own sheet. */}
                <button
                  type="button"
                  className="pdc-pill flex-1 min-w-0"
                  onClick={() => setSheetOpen(0)}
                  aria-haspopup="dialog"
                  aria-label={`${t('appsWindow.sheet.show')}: ${chipLabel[chip]}`}
                  data-testid="apps-sheet-open"
                >
                  <span className="pdc-pill-icon">{chipIcon[chip]}</span>
                  <span className="truncate">{chipLabel[chip]}</span>
                  <span className="pdc-pill-count">{chipViews[chip].length}</span>
                  <ChevronDown size={14} className="shrink-0 opacity-60" />
                </button>
                <button
                  type="button"
                  className="pdc-pill shrink-0"
                  onClick={() => setSheetOpen(1)}
                  aria-haspopup="dialog"
                  aria-label={`${t('appsWindow.sort.label')}: ${t(`appsWindow.sort.${sort}`)}`}
                  data-testid="apps-sheet-sort"
                >
                  <ArrowUpDown size={14} className="shrink-0 opacity-70" />
                  <span>{t(`appsWindow.sort.${sort}`)}</span>
                  <ChevronDown size={14} className="shrink-0 opacity-60" />
                </button>
                {sheetOpen !== null && (
                  <FilterSheet
                    title={sheetOpen === 0 ? t('appsWindow.sheet.show') : t('appsWindow.sort.label')}
                    themeClass={themeClass}
                    onClose={() => setSheetOpen(null)}
                    options={sheetOpen === 0
                      ? CHIPS.map(c => ({ id: c, label: chipLabel[c], icon: chipIcon[c], count: chipViews[c].length, selected: chip === c, onSelect: () => setChip(c), testId: `apps-sheet-chip-${c}` }))
                      : COLLECTION_SORTS.map(o => ({ id: o, label: t(`appsWindow.sort.${o}`), selected: sort === o, onSelect: () => setSort(o), testId: `apps-sheet-sort-${o}` }))}
                  />
                )}
              </div>
            ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <div
                role="group"
                aria-label={orgLabel ?? t('appsWindow.title')}
                className="flex items-center gap-1.5 flex-wrap"
                data-testid="apps-tabs"
              >
                {CHIPS.map(c => (
                  <button key={c} type="button" aria-pressed={chip === c} onClick={() => setChip(c)}
                    className="ws-filter-chip inline-flex items-center gap-1.5 shrink-0" data-testid={`apps-tab-${c}`}>
                    {chipIcon[c]}{chipLabel[c]}<span className="opacity-60">{chipViews[c].length}</span>
                  </button>
                ))}
              </div>
              <button
                ref={sortBtnRef}
                type="button"
                className="pdc-sort"
                onClick={() => setSortOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={sortOpen}
                aria-label={`${t('appsWindow.sort.label')}: ${t(`appsWindow.sort.${sort}`)}`}
                data-testid="apps-sort"
              >
                <span className="pdc-sort-label">{t('appsWindow.sort.label')}</span>
                <span>{t(`appsWindow.sort.${sort}`)}</span>
                <ChevronDown size={13} className="opacity-70" />
              </button>
            </div>
            )}
            <div data-testid={`apps-group-${chip}`}>
              {collection.length > 0 ? renderGrid('collection', collection, undefined, true, undefined, 4) : <p className="text-sm themed-text-muted py-6">{t('appsWindow.tabEmpty')}</p>}
              {(chip === 'links' || chip === 'shared') && (
                <p className="mt-6 flex items-center gap-1.5 text-xs themed-text-muted" data-testid="apps-public-hint">
                  <Globe size={12} className="shrink-0" />
                  {t('appsWindow.publicLinksHint')}
                </p>
              )}
            </div>
          </section>
        )}
      </>
    )
  }

  /** The Recent page: the whole history, filter chips, Show more, Clear history. */
  const recentPage = () => {
    const counts = Object.fromEntries(RECENT_FILTERS.map(f => [f, 0])) as Record<RecentFilter, number>
    for (const v of recentViews) {
      counts.all++
      if (v.tile.type === 'entry') counts[recentFilterOf(v.tile.entry, tenantId)]++
    }
    const shown = recentFilter === 'all'
      ? recentViews
      : recentViews.filter(v => v.tile.type === 'entry' && recentFilterOf(v.tile.entry, tenantId) === recentFilter)
    const chipLabel = (f: RecentFilter) =>
      f === 'here' ? orgLabel ?? t('appsWindow.recentPage.filter.here') : t(`appsWindow.recentPage.filter.${f}`)
    return (
      <section data-testid="apps-recent-page">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div role="group" aria-label={t('appsWindow.recentPage.filter.label')} className="flex items-center gap-1.5 flex-wrap" data-testid="apps-recent-filter">
            {RECENT_FILTERS.map(f => (
              <button key={f} type="button" aria-pressed={recentFilter === f} onClick={() => setRecentFilter(f)}
                className="ws-filter-chip inline-flex items-center gap-1.5" data-testid={`apps-recent-filter-${f}`}>
                {chipLabel(f)}<span className="opacity-60">{counts[f]}</span>
              </button>
            ))}
          </div>
          {recentViews.length > 0 && !query.trim() && (
            <button onClick={() => void onClear()} className={linkBtn} data-testid="apps-clear-history">
              <Trash2 size={12} className="mr-0.5" />
              {t('appsWindow.clear.button')}
            </button>
          )}
        </div>
        {shown.length > 0
          ? renderGrid('results', shown)
          : !moreHistory && <p className="text-sm themed-text-muted py-6">{query.trim() ? t('appsWindow.noMatches', { query: query.trim() }) : t('appsWindow.tabEmpty')}</p>}
        {moreHistory && (
          <button onClick={() => void showMore()} disabled={loadingMore || rereading} className="mt-6 themed-btn-secondary rounded-lg px-3 py-1.5 text-xs disabled:opacity-60" data-testid="apps-show-more">
            {loadingMore ? t('appsWindow.loading') : t('appsWindow.showMore')}
          </button>
        )}
      </section>
    )
  }

  const body = () => {
    if (loading) return <p className="text-sm themed-text-muted">{t('appsWindow.loading')}</p>
    if (history.isError && (historyOnly || projects.isError)) return <p className="text-sm themed-text-muted">{t('appsWindow.error')}</p>
    if (onRecentPage) return recentPage()
    if (nothing && query.trim()) return <p className="text-sm themed-text-muted py-8 text-center">{t('appsWindow.noMatches', { query: query.trim() })}</p>
    // Links that name the person arrive last (each judged on the server): until they
    // have, an empty page is "loading", never "start your collection".
    if (nothing && !allLoaded) return <p className="text-sm themed-text-muted">{t('appsWindow.loading')}</p>
    if (nothing) {
      return (
        <div className="pdc-empty" data-testid="apps-empty">
          <div className="pdc-empty-cards" aria-hidden>
            {[0, 1, 2].map(i => <GhostCard key={i} width={cardW} />)}
          </div>
          <h2 className="text-lg font-semibold themed-text">{t('appsWindow.empty.start')}</h2>
          <p className="mt-1.5 text-sm themed-text-muted max-w-sm mx-auto">{t('appsWindow.empty.hint')}</p>
          {!historyOnly && (
            <button
              type="button"
              onClick={() => newProject.open({ entityKind: 'miniapp', onDone: (fid) => navigate(`${urlPrefix}/workspace/${fid}`) })}
              className="themed-btn-primary rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 mt-5"
              data-testid="apps-empty-new"
            >
              <Plus size={15} />
              {t('driveView.actions.newMiniapp', 'New PDApp')}
            </button>
          )}
        </div>
      )
    }
    return query.trim() ? results() : lists()
  }

  const quickTarget = quickShare ? shareTargetFor(quickShare.tile) : null
  const quickRow = quickShare ? driveRowFor(quickShare.tile) : null
  // Share & access: who may open Who has access is asked of the server for THIS app (the drive's
  // own rule — managers only); asked only while the modal is open on such an app.
  const quickAccessRow = quickShare ? accessRowFor(quickShare.tile) : null
  const quickPerm = usePermission(accessFor?.id ?? quickAccessRow?.id ?? null)
  // Every card the page shows, by id — the flipped one is looked up fresh each render
  // (a Favorite changes it in place; a removal closes it).
  const inspected = inspect
    ? [...recentViews, ...pinnedViews, ...mineViews, ...sharedViews, ...linkViews].find(v => v.id === inspect.id) ?? null
    : null
  // A card whose app leaves the lists (an Unfavorite that drops a never-opened entry, a
  // refetch that judges it out) closes — still drawn with the last view it showed, so the
  // close runs to its end and the window lets go of it.
  const lastInspected = useRef<TileView | null>(null)
  if (inspected) lastInspected.current = inspected
  const shownInspected = inspected ?? (inspect && lastInspected.current?.id === inspect.id ? lastInspected.current : null)
  // The flipped card's app moved section while its back was open (a Favorite moves it from
  // Recent to the deck, an Unfavorite back): follow it — it is that card that steps aside,
  // that the close flies back to and that gets the focus.
  useEffect(() => {
    if (!inspect || inspect.closing || !rootEl) return
    if (rootEl.querySelector(`.pdc[data-place="${CSS.escape(inspect.place)}"]`)) return
    const moved = rootEl.querySelector<HTMLElement>(`.pdc[data-card-id="${CSS.escape(inspect.id)}"][data-place]`)
    if (!moved) return
    inspectSource.current = moved.querySelector<HTMLElement>('.pdc-hit')
    const place = moved.dataset.place!
    const from = moved.getBoundingClientRect()
    setInspect(cur => (cur && cur.id === inspect.id && !cur.closing ? { ...cur, place, from } : cur))
  })
  useEffect(() => {
    if (!inspect || inspected) return
    if (!inspect.closing) closeInspect()
    // Nothing left to draw it with: let go at once.
    if (!shownInspected) { pushedRef.current = false; setInspect(null) }
  })

  const searchLabel = onRecentPage ? t('appsWindow.recentPage.search') : t('appsWindow.search')
  return (
    <>
    <div
      ref={setRootEl}
      // `true`, never '' (React 19 drops an empty boolean attribute). The dialogs portal out of it.
      inert={overDialog ? true : undefined}
      className={`flex-1 min-h-0 overflow-y-auto papan-thin-scroll${gyro.on ? ' pdc-gyro' : ''}${dealing ? ' pdc-dealing' : ''}`}
      // Focus and the arrow keys scroll a card clear of the sticky top bar (its measured height).
      style={{ '--grid-card-w': `${cardW}px`, '--narrow-cols': narrowCols, scrollPaddingTop: topbarH + 12, scrollPaddingBottom: 16 } as CSSProperties}
      onKeyDown={onCardKeys}
      data-testid="apps-root"
    >
      {/* The page's light: the colours of the card under the pointer, behind everything. */}
      <div className="pdc-ambient" aria-hidden><div ref={ambRef} className="pdc-ambient-glow" /></div>
      <div className="sr-only" aria-live="polite" data-testid="apps-announce">{announce}</div>
      <div ref={setTopbarEl} className="pdc-topbar">
        <div className={`w-full max-w-[1600px] mx-auto ${narrow ? 'px-4' : 'px-4 sm:px-8'} py-3 flex items-center gap-x-4 gap-y-2.5 flex-wrap`}>
          {onRecentPage ? (
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                // Came from the main page ("View all", which noted the main page's history
                // index): step back to it — over any card entries left on top — so Back never
                // returns here. Otherwise (a link, a new tab): go there in place of this page.
                onClick={() => {
                  const st = location.state as { pdappsFromMain?: unknown; pdappsCard?: unknown } | null
                  const from = st?.pdappsFromMain
                  const at = historyIndex()
                  if (Number.isInteger(from) && at !== null && at > (from as number)) navigate((from as number) - at)
                  // Came from the main page but the index can't be counted: one step, if no card entry is on top.
                  else if (from != null && !st?.pdappsCard) navigate(-1)
                  else navigate(`${urlPrefix}/apps`, { replace: true })
                }}
                className="ws-card-iconbtn -ml-1.5"
                aria-label={t('appsWindow.recentPage.back')}
                title={t('appsWindow.recentPage.back')}
                data-testid="apps-recent-back"
              >
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-xl font-semibold themed-text truncate">{t('appsWindow.recentPage.title')}</h1>
            </div>
          ) : (
            <div className={`flex items-center gap-2.5 min-w-0 ${narrow ? 'flex-1' : ''}`}>
              <PDAppIcon size={20} className="text-fuchsia-400 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-semibold themed-text leading-tight">{t('appsWindow.title')}</h1>
                {allLoaded && collectedCount > 0 && (
                  <CollectionCounter count={collectedCount} favorites={favCount} ready={allLoaded} played={counterPlayed} />
                )}
              </div>
            </div>
          )}
          <div className={`relative ${narrow ? 'order-last w-full' : 'flex-1 max-w-md ml-auto'}`}>
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery('') } }}
              placeholder={searchLabel}
              aria-label={searchLabel}
              data-testid="apps-search"
              className="w-full themed-input ws-drive-pill rounded-lg pl-9 pr-9 py-2 text-sm"
            />
            {!narrow && !query && <kbd className="pdc-kbd absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden>/</kbd>}
          </div>
          <div className={`flex items-center gap-2 ${narrow ? 'ml-auto' : ''}`}>
            {gyro.supported && (
              <button
                type="button"
                onClick={gyro.toggle}
                aria-pressed={gyro.on}
                aria-label={t('appsWindow.tilt.label')}
                title={t('appsWindow.tilt.label')}
                className={`rounded-lg px-2.5 py-2 text-xs flex items-center gap-1.5 ${gyro.on ? 'themed-btn-primary' : 'themed-btn-secondary'}`}
                data-testid="apps-tilt"
              >
                <Smartphone size={14} />
                {!narrow && (gyro.on ? t('appsWindow.tilt.on') : t('appsWindow.tilt.off'))}
              </button>
            )}
            {/* Everything this window leaves out (rename, move, icon, access…) lives there. */}
            {!historyOnly && !onRecentPage && (
              <button
                onClick={() => navigate(`${urlPrefix}/pdapps`)}
                className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5 whitespace-nowrap"
                data-testid="apps-manage"
              >
                {narrow ? t('appsWindow.manageShort') : t('appsWindow.manage')}
                <ArrowUpRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
      {/* The phone layout's 16 px gutters follow the WINDOW's width (the card maths), not the screen's. */}
      <div className={`relative w-full max-w-[1600px] mx-auto ${narrow ? 'px-4 py-6' : 'px-4 py-6 sm:px-8 sm:py-8'}`}>
        {body()}
      </div>

      {sortOpen && (
        <FileContextMenu
          x={sortPos.pos?.left ?? -9999}
          y={sortPos.pos?.top ?? -9999}
          exiting={sortPos.exiting}
          origin={sortPos.origin}
          items={sortItems}
          menuRef={sortMenuRef}
          className={themeClass}
          testId="apps-sort-menu"
        />
      )}
      {inspect && shownInspected && (
        <CardInspect
          view={shownInspected}
          from={inspect.from}
          themeClass={themeClass}
          labels={cardLabels}
          details={detailsFor(shownInspected)}
          actions={actionsFor(shownInspected)}
          favorite={favoriteFor(shownInspected)}
          closing={inspect.closing}
          quick={inspect.quick}
          onRequestClose={() => closeInspect()}
          onClosed={onInspectClosed}
        />
      )}
      {quickShare && quickTarget && (
        <AppShareDialog
          name={quickShare.name}
          icon={quickShare.icon}
          iconRef={quickShare.iconRef}
          source={quickShare.hint ?? (orgLabel ?? undefined)}
          target={quickTarget}
          onClose={() => setQuickShare(null)}
          onShareWithPeople={quickTarget.kind === 'own' && quickTarget.canShare && quickRow
            ? () => { setQuickShare(null); setFullShare(quickRow) }
            : undefined}
          access={quickAccessRow
            ? {
                // Unresolved (loading or failed) is never "may manage" — the row waits, disabled.
                canManage: quickPerm.data !== undefined ? canManageAccessFor(quickPerm.data) : undefined,
                onOpen: () => { setQuickShare(null); setAccessFor({ id: quickAccessRow.id, name: quickAccessRow.name, path: quickAccessRow.path }) },
              }
            : undefined}
          onShareFromThere={quickTarget.kind === 'elsewhere' && quickShare.tile.type === 'entry' && quickShare.tile.entry.app
            ? () => {
                const where = (quickShare.tile as { type: 'entry'; entry: PdappEntry }).entry.app!
                setQuickShare(null)
                goTo(`${prefixOf(where)}/apps?share=${encodeURIComponent(where.appId)}`, where)
              }
            : undefined}
        />
      )}
      {/* The drive's own Who has access dialog, as its row menu opens it for this folder. */}
      {accessFor && (
        <WhoHasAccessDialog
          folderId={accessFor.id}
          folderName={accessFor.name}
          folderPath={accessFor.path}
          tenantId={tenantId}
          isFolder
          myPermission={quickPerm.data}
          isReceivedShare={isSharedPointer(accessFor.name)}
          inShareContext={isSharedPointer(accessFor.name)}
          hidePeopleTeams={!isOrg}
          onClose={() => setAccessFor(null)}
        />
      )}
      {/* The same dialog a running app's Share button opens; it applies its own rules. */}
      {fullShare && (
        <ShareLinksDialog
          fileId={fullShare.id}
          fileName={fullShare.name}
          filePath={fullShare.path}
          isFolder={fullShare.isFolder}
          // The drive card's rule for offering an App link; the dialog decides the rest.
          isAppEligible={isMiniappFolder(fullShare.name)}
          onClose={() => setFullShare(null)}
        />
      )}
    </div>
    {/* More's dialogs (the drive's own), outside the window: it is inert while one is open. */}
    {cardDialogs}
    </>
  )
}
